import { useCallback, useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useUrlParam } from '../../lib/useUrlTab.js';
import { useQuery } from '@tanstack/react-query';
import { fetchSessionFull, fetchTimeline, type TimelineSpan } from '../../api/admin.js';
import {
  COPY_VIEW_OPTIONS,
  deriveCopyPayload,
  type CopyView,
} from '../../lib/session-copy.js';
import { Waterfall, type RowVisibility } from '../../components/timeline/Waterfall.js';
import { Tree } from '../../components/timeline/Tree.js';
import { SpanDetailPane } from '../../components/timeline/SpanDetailPane.js';
import { LiveTimeline } from '../../components/timeline/LiveTimeline.js';
import { SpanDetailDrawer } from '../../components/timeline/SpanDetailDrawer.js';
import { useLiveSession, type LiveSpanCloseEvent } from '../../lib/useLiveSession.js';
import { SessionReplayModal } from '../../components/ui/SessionReplayModal.js';
import { DistillPanel } from '../../components/distill/DistillPanel.js';
import { SessionFindingsSection } from '../../components/findings/SessionFindingsSection.js';
import { CostDonut } from '../../components/session/CostDonut.js';
import { CostOriginDonut } from '../../components/session/CostOriginDonut.js';
import { TimeStackedBar } from '../../components/session/TimeStackedBar.js';
import { SessionChatModal } from '../../components/ui/SessionChatModal.js';
import { TurnsTable } from '../../components/session/TurnsTable.js';
import { summarizeTurns, totalCostFromSpans } from '../../lib/session-stats.js';
import { kindColor, SPAN_KINDS } from '../../lib/spanKind.js';
import {
  formatDurationMs,
  formatNumber,
  nanoToUsdCompact,
  shortId,
} from '../../lib/format.js';
import { preferRawCost } from '../../lib/cost-display.js';
import { GATED_CONTENT_PLACEHOLDER, isGatedModule } from '../../lib/privacy.js';

type ViewMode = 'live' | 'waterfall' | 'tree';
const ALL_VIEW_MODES: readonly ViewMode[] = ['live', 'waterfall', 'tree'];

/** Formatea un conteo de tokens compacto: 48213 → "48k", 950 → "950". */
function compactTokens(n: number): string {
  if (n >= 1000) {
    const k = n / 1000;
    return `${k >= 10 ? Math.round(k) : k.toFixed(1)}k`;
  }
  return String(n);
}

const DEFAULT_KINDS = new Set<string>([
  'session',
  'turn',
  'llm',
  'stt',
  'tts',
  'pronunciation',
  'composite',
  'tool_call',
  'retrieval',
  'guardrail',
]);

export function SessionDetailPage(): ReactElement {
  const { id } = useParams<{ id: string }>();
  // View mode con default dinámico: si la sesión está en curso, abre en
  // 'live'; si está finalizada, abre en 'waterfall'. URL `?view=...` overridea.
  // No usamos `useUrlTab` porque el default depende del estado de la
  // sesión, que no conocemos al render inicial.
  const [viewParam, setViewParam] = useUrlParam('view');
  const [selectedId, setSelectedId] = useUrlParam('span');
  const [selectedTurnId, setSelectedTurnId] = useUrlParam('turn');
  const [enabledKinds, setEnabledKinds] = useState<Set<string>>(DEFAULT_KINDS);
  const [onlyErrors, setOnlyErrors] = useState(false);
  const [hideNonMatching, setHideNonMatching] = useState(false);
  const [search, setSearch] = useState('');
  const [replayOpen, setReplayOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [copyState, setCopyState] = useState<'idle' | 'copying' | 'copied' | 'error'>('idle');
  const [copyMenuOpen, setCopyMenuOpen] = useState(false);
  const [copiedLabel, setCopiedLabel] = useState<string | null>(null);
  const copyMenuRef = useRef<HTMLDivElement | null>(null);

  // Cerrar el menú con click fuera o ESC.
  useEffect(() => {
    if (!copyMenuOpen) return;
    const onClick = (e: MouseEvent): void => {
      if (
        copyMenuRef.current &&
        !copyMenuRef.current.contains(e.target as Node)
      ) {
        setCopyMenuOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setCopyMenuOpen(false);
    };
    window.addEventListener('mousedown', onClick);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onClick);
      window.removeEventListener('keydown', onKey);
    };
  }, [copyMenuOpen]);

  const handleCopyView = useCallback(
    async (view: CopyView, label: string): Promise<void> => {
      if (!id) return;
      setCopyMenuOpen(false);
      setCopyState('copying');
      try {
        const dump = await fetchSessionFull(id, { includePrompts: true });
        const payload = deriveCopyPayload(view, dump);
        await navigator.clipboard.writeText(payload);
        setCopiedLabel(label);
        setCopyState('copied');
        setTimeout(() => {
          setCopyState('idle');
          setCopiedLabel(null);
        }, 2200);
      } catch (err) {
        console.warn('[copy-json] failed', view, err);
        setCopyState('error');
        setTimeout(() => setCopyState('idle'), 2500);
      }
    },
    [id],
  );

  const query = useQuery({
    queryKey: ['admin', 'timeline', id],
    queryFn: () => fetchTimeline(id ?? ''),
    enabled: Boolean(id),
    // Polling más agresivo (2s) cuando la sesión está en curso para alimentar
    // el tab Live. F4 reemplaza esto con SSE; mientras tanto el polling es
    // la fuente del feed live.
    refetchInterval: (q) => (q.state.data?.session.endedAt === null ? 2_000 : false),
  });

  const spans = query.data?.spans ?? [];

  // "errors N" del header → salta al primer span con error: activa "Only
  // errors", garantiza que su kind esté habilitado (si no, matchKind lo
  // seguiría ocultando) y lo selecciona para abrir el drawer de detalle.
  const handleJumpToError = useCallback((): void => {
    const firstError = spans.find((s) => s.isError);
    if (!firstError) return;
    setOnlyErrors(true);
    setEnabledKinds((prev) =>
      prev.has(firstError.kind) ? prev : new Set(prev).add(firstError.kind),
    );
    setSelectedId(firstError.id);
  }, [spans, setSelectedId]);

  // View efectivo: URL gana; si no hay URL, depende de si la sesión está
  // en curso (live por default) o finalizada (waterfall por default).
  const sessionIsLive = query.data?.session.endedAt === null;
  const view: ViewMode = useMemo(() => {
    if (viewParam && (ALL_VIEW_MODES as readonly string[]).includes(viewParam)) {
      return viewParam as ViewMode;
    }
    return sessionIsLive ? 'live' : 'waterfall';
  }, [viewParam, sessionIsLive]);
  const setView = useCallback(
    (next: ViewMode): void => {
      // Mantener URL limpia: si el next coincide con el default actual,
      // borrarlo del query string.
      const defaultView: ViewMode = sessionIsLive ? 'live' : 'waterfall';
      setViewParam(next === defaultView ? null : next);
    },
    [sessionIsLive, setViewParam],
  );

  // Fase 42 — SSE alimenta el tab Live con spans cerrados en tiempo real.
  // Sólo conectamos si estamos viendo el tab Live (no gastar canales en
  // Waterfall/Tree, que no los necesitan).
  const liveSession = useLiveSession(id ?? null, view === 'live' && sessionIsLive);

  // Merge polling + SSE: para el tab Live, combinamos los spans que vienen
  // de `/timeline` (snapshot persistido) con los `span.close` que llegan
  // por SSE (eventos en vivo entre polls). Dedup por `id`. Los SSE pueden
  // llegar antes que el próximo poll, lo cual hace que el Live se sienta
  // instantáneo.
  const liveSpans = useMemo<TimelineSpan[]>(() => {
    if (view !== 'live') return spans;
    const byId = new Map<string, TimelineSpan>();
    for (const s of spans) byId.set(s.id, s);
    for (const [spanId, ev] of liveSession.spanCloses) {
      if (byId.has(spanId)) continue;
      byId.set(spanId, liveEventToTimelineSpan(ev));
    }
    return Array.from(byId.values()).sort((a, b) =>
      a.startedAt.localeCompare(b.startedAt),
    );
  }, [view, spans, liveSession.spanCloses]);

  // Toast cuando la sesión termina mientras estamos en el tab Live.
  // No forzamos el switch — el usuario decide.
  const prevIsLiveRef = useRef<boolean | null>(null);
  const [endedToast, setEndedToast] = useState<boolean>(false);
  useEffect(() => {
    if (query.data?.session == null) return;
    const isLive = query.data.session.endedAt === null;
    if (prevIsLiveRef.current === true && !isLive && view === 'live') {
      setEndedToast(true);
      const t = window.setTimeout(() => setEndedToast(false), 6_000);
      return () => window.clearTimeout(t);
    }
    prevIsLiveRef.current = isLive;
    return undefined;
  }, [query.data?.session, view]);

  // Set de spanIds del turn seleccionado. Si no hay turn seleccionado,
  // el set queda vacío y rowVisibility lo trata como "todos visibles".
  const turnSpanIds = useMemo<Set<string> | null>(() => {
    if (!selectedTurnId) return null;
    const turns = summarizeTurns(spans);
    const match = turns.find((t) => t.turnId === selectedTurnId);
    if (!match) return null;
    return new Set(match.spanIds);
  }, [spans, selectedTurnId]);

  const selectedSpan = useMemo<TimelineSpan | null>(() => {
    if (!selectedId) return null;
    // En Live buscamos en el set merged (polling + SSE). En otras vistas
    // basta con lo que devolvió /timeline.
    const pool = view === 'live' ? liveSpans : query.data?.spans ?? [];
    return pool.find((s) => s.id === selectedId) ?? null;
  }, [view, liveSpans, query.data, selectedId]);

  const rowVisibility = useCallback<(span: TimelineSpan) => RowVisibility>(
    (span) => {
      const matchKind = enabledKinds.has(span.kind);
      const matchError = !onlyErrors || span.isError;
      const matchSearch =
        search.trim().length === 0 || span.name.toLowerCase().includes(search.trim().toLowerCase());
      const matchTurn = turnSpanIds === null || turnSpanIds.has(span.id);
      const visible = matchKind && matchError && matchSearch && matchTurn;
      if (visible) return 'visible';
      return hideNonMatching || turnSpanIds !== null ? 'hidden' : 'dim';
    },
    [enabledKinds, onlyErrors, hideNonMatching, search, turnSpanIds],
  );

  if (!id) return <div className="loading">Falta session id</div>;
  if (query.isPending) return <div className="loading">Cargando…</div>;
  if (query.isError) return <div className="loading">Error: {query.error.message}</div>;

  const sess = query.data.session;
  const moduleLabel = sess.moduleId ?? 'unknown';
  const hasSpans = query.data.spans.length > 0;
  // Gate de privacidad (claim de Coach): en sesiones de módulos sensibles
  // el backend rechaza transcript/replay con 403 gated_module — acá ni
  // ofrecemos los botones; mostramos el placeholder en su lugar.
  const gated = isGatedModule(sess.moduleId);

  return (
    <>
      <header className="sess-header" data-testid="sess-header">
        <span className="badge b-mod">module · {moduleLabel}</span>
        <span className="sid">{shortId(sess.id, 24)}</span>
        <div className="meta">
          <span>
            <span className="key">user</span>
            <Link to={`/users/${sess.userId}`} className="val">
              {sess.userEmail}
            </Link>
          </span>
          <span>
            <span className="key">started</span>
            <span className="val">{new Date(sess.startedAt).toLocaleTimeString()}</span>
          </span>
          <span>
            <span className="key">duration</span>
            <span className="val">{formatDurationMs(sess.totalDurationMs)}</span>
          </span>
          <span>
            <span className="key">cost</span>
            <span className="val" title="Suma raw de TODOS los leaf spans (incluye distill y jobs auxiliares). Coincide con el total del donut.">
              {nanoToUsdCompact(
                hasSpans
                  ? totalCostFromSpans(spans).toString()
                  : preferRawCost(sess.totalCostRawNano, sess.totalCostNano),
              )}
            </span>
          </span>
          {(() => {
            const cIn = sess.tokensIn ?? 0;
            const cRead = sess.cacheReadTokens ?? 0;
            const cWrite = sess.cacheWriteTokens ?? 0;
            if (cIn + cRead + cWrite === 0) return null;
            const denom = cRead + cIn;
            const hitPct = denom > 0 ? Math.round((cRead / denom) * 100) : 0;
            return (
              <span>
                <span className="key">cache</span>
                <span
                  className="val"
                  title={
                    `Prompt cache de Anthropic. hit-rate = cache-read / (cache-read + input no cacheado). ` +
                    `El costo de LLM YA descuenta esto: los tokens cache-read se cobran a ~1/10 de la tarifa input. ` +
                    `input fresco ${compactTokens(cIn)} · cache-read ${compactTokens(cRead)} · cache-write ${compactTokens(cWrite)}.`
                  }
                >
                  {hitPct}% · {compactTokens(cRead)}↓ / {compactTokens(cWrite)}↑
                </span>
              </span>
            );
          })()}
          {(sess.parentSessionId || (sess.childSessionIds?.length ?? 0) > 0) && (
            <span>
              <span className="key">hilo</span>
              <span className="val" style={{ display: 'inline-flex', gap: 10 }}>
                {sess.parentSessionId ? (
                  <Link
                    to={`/sessions/${sess.parentSessionId}`}
                    title="Sesión anterior — ésta es su continuación (resume)"
                  >
                    ↩ {shortId(sess.parentSessionId, 8)}
                  </Link>
                ) : null}
                {(sess.childSessionIds ?? []).map((cid) => (
                  <Link key={cid} to={`/sessions/${cid}`} title="Continuación de esta sesión">
                    → {shortId(cid, 8)}
                  </Link>
                ))}
              </span>
            </span>
          )}
          <span>
            <span className="key">turns</span>
            <span className="val">{formatNumber(sess.turnCount)}</span>
          </span>
          <span>
            <span className="key">errors</span>
            {sess.errorCount > 0 ? (
              <button
                type="button"
                className="badge b-error badge-btn"
                onClick={handleJumpToError}
                data-testid="jump-to-error"
                title="Ver el primer error de la sesión"
              >
                {sess.errorCount}
              </button>
            ) : (
              <span className="val">0</span>
            )}
          </span>
          {sess.endedAt === null ? (
            <span className="badge b-ok">
              <span className="dot-live" /> live
            </span>
          ) : null}
        </div>
        <div className="right">
          {gated ? (
            <span
              className="badge b-neutral"
              data-testid="session-gated-placeholder"
              title="El contenido conversacional de este módulo no está disponible en el admin. Costos, latencias y timeline siguen visibles."
            >
              🔒 {GATED_CONTENT_PLACEHOLDER}
            </span>
          ) : (
            <>
              <button
                type="button"
                className="btn"
                onClick={() => setReplayOpen(true)}
                data-testid="session-replay-open"
              >
                ▶ Replay
              </button>
              <button
                type="button"
                className="btn"
                onClick={() => setChatOpen(true)}
                data-testid="session-chat-open"
                title="Ver toda la conversación como chat"
              >
                💬 Chat
              </button>
            </>
          )}
          <div className="copy-menu-wrap" ref={copyMenuRef}>
            <button
              type="button"
              className="btn"
              onClick={() => setCopyMenuOpen((v) => !v)}
              disabled={copyState === 'copying'}
              data-testid="session-copy-json"
              aria-haspopup="menu"
              aria-expanded={copyMenuOpen}
              title="Elegir qué copiar al portapapeles"
            >
              {copyState === 'copied'
                ? `✓ ${copiedLabel ?? 'Copiado'}`
                : copyState === 'error'
                  ? '✗ Error'
                  : copyState === 'copying'
                    ? 'Copiando…'
                    : '⧉ Copy ▾'}
            </button>
            {copyMenuOpen ? (
              <div className="copy-menu" role="menu" data-testid="session-copy-menu">
                {COPY_VIEW_OPTIONS.map((opt) => (
                  <button
                    type="button"
                    key={opt.view}
                    role="menuitem"
                    className="copy-menu-item"
                    onClick={() => void handleCopyView(opt.view, opt.label)}
                    data-testid={`session-copy-${opt.view}`}
                  >
                    <span className="copy-menu-label">{opt.label}</span>
                    <span className="copy-menu-desc">{opt.description}</span>
                    <span className="copy-menu-format">{opt.format}</span>
                  </button>
                ))}
              </div>
            ) : null}
          </div>
          <Link to={`/users/${sess.userId}`} className="btn">
            Open user
          </Link>
          <div className="seg-wf" role="group" aria-label="View mode">
            <button
              type="button"
              className={view === 'live' ? 'is-on' : ''}
              onClick={() => setView('live')}
              data-testid="view-live"
              title={sessionIsLive ? 'Vista en vivo' : 'Replay narrativo (sesión finalizada)'}
            >
              {sessionIsLive ? <span className="dot-live" /> : null} Live
            </button>
            <button
              type="button"
              className={view === 'waterfall' ? 'is-on' : ''}
              onClick={() => setView('waterfall')}
              data-testid="view-waterfall"
            >
              Waterfall
            </button>
            <button
              type="button"
              className={view === 'tree' ? 'is-on' : ''}
              onClick={() => setView('tree')}
              data-testid="view-tree"
            >
              Tree
            </button>
          </div>
        </div>
      </header>

      {hasSpans ? (
        <section className="sess-charts" data-testid="sess-charts">
          <CostDonut spans={spans} />
          <CostOriginDonut spans={spans} />
          <TimeStackedBar spans={spans} />
        </section>
      ) : null}

      {hasSpans ? (
        <section className="sess-turns" data-testid="sess-turns">
          <TurnsTable
            spans={spans}
            selectedTurnId={selectedTurnId}
            onSelectTurn={(turnId) => {
              setSelectedTurnId(turnId);
              if (turnId) setView('waterfall');
            }}
            onSelectSpan={(spanId) => {
              setView('waterfall');
              setSelectedId(spanId);
            }}
          />
        </section>
      ) : null}

      <section className="sess-filters" data-testid="sess-filters">
        <span className="label">Kinds</span>
        <div className="flex gap-2" style={{ flexWrap: 'wrap' }}>
          {SPAN_KINDS.filter((k) => DEFAULT_KINDS.has(k) || k === 'tool_call' || k === 'retrieval' || k === 'guardrail').map(
            (kind) => {
              const on = enabledKinds.has(kind);
              return (
                <button
                  key={kind}
                  type="button"
                  className={`chip${on ? ' is-on' : ''}`}
                  onClick={() => toggleSetMember(setEnabledKinds, kind)}
                  data-testid={`chip-kind-${kind}`}
                >
                  <span
                    className="chip-dot"
                    style={{ background: kindColor(kind) }}
                    aria-hidden="true"
                  />
                  {kind}
                </button>
              );
            },
          )}
        </div>
        <span className="divider" aria-hidden="true" />
        <label className="toggle">
          <input
            type="checkbox"
            checked={onlyErrors}
            onChange={(e) => setOnlyErrors(e.target.checked)}
            data-testid="toggle-only-errors"
          />
          <span className="toggle-slider" />
          Only errors
        </label>
        <label className="toggle">
          <input
            type="checkbox"
            checked={hideNonMatching}
            onChange={(e) => setHideNonMatching(e.target.checked)}
            data-testid="toggle-hide-nonmatching"
          />
          <span className="toggle-slider" />
          Hide non-matching
        </label>
        {selectedTurnId !== null ? (
          <span className="badge b-info" data-testid="sess-filter-turn">
            turn filtrado · <button
              type="button"
              className="link-btn"
              onClick={() => setSelectedTurnId(null)}
            >
              ✕
            </button>
          </span>
        ) : null}
        <div className="group" style={{ marginLeft: 'auto' }}>
          <input
            type="text"
            placeholder="search by name…"
            style={{ width: 220 }}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="search-name"
          />
        </div>
      </section>

      {query.data.truncated || query.data.holes.length > 0 ? (
        <div className="filterbar" style={{ minHeight: 32 }}>
          {query.data.truncated ? <span className="badge b-warn">truncated</span> : null}
          {query.data.holes.length > 0 ? (
            <span className="badge b-info">{query.data.holes.length} holes (sampling)</span>
          ) : null}
        </div>
      ) : null}

      {view === 'live' ? (
        <>
          <LiveTimeline
            spans={liveSpans}
            sessionIsLive={sessionIsLive}
            selectedSpanId={selectedId}
            onSelectSpan={setSelectedId}
          />
          <SpanDetailDrawer
            span={selectedSpan}
            liveEvent={selectedId ? liveSession.spanCloses.get(selectedId) : undefined}
            onClose={() => setSelectedId(null)}
          />
        </>
      ) : (
        <div className={`sess-body${selectedSpan ? '' : ' no-detail'}`}>
          <div>
            {view === 'waterfall' ? (
              <Waterfall
                spans={query.data.spans}
                selectedId={selectedId}
                onSelect={setSelectedId}
                rowVisibility={rowVisibility}
              />
            ) : (
              <div style={{ padding: 'var(--s-5) var(--s-7)' }}>
                <Tree spans={query.data.spans} selectedId={selectedId} onSelect={setSelectedId} />
              </div>
            )}
            {query.data.spans.length === 0 ? (
              <div className="empty" data-testid="timeline-empty">
                Sin spans persistidos. La sesión existe pero tracing estaba apagado o sampled
                out. Session id <span className="mono">{shortId(id, 12)}</span>.
              </div>
            ) : null}
          </div>
          {selectedSpan ? (
            <SpanDetailPane span={selectedSpan} onClose={() => setSelectedId(null)} />
          ) : null}
        </div>
      )}

      {endedToast ? (
        <div className="live-tl-toast" role="status" data-testid="live-tl-ended-toast">
          Sesión finalizada · podés cambiar a <button
            type="button"
            className="link-btn"
            onClick={() => setView('waterfall')}
          >
            Waterfall
          </button>
        </div>
      ) : null}

      <section
        className="sess-distill"
        style={{ padding: 'var(--s-4) var(--s-6)' }}
      >
        <DistillPanel
          sessionId={id}
          onJumpToWaterfall={(spanId) => {
            setView('waterfall');
            setSelectedId(spanId);
          }}
        />
      </section>

      <SessionFindingsSection sessionId={id} moduleId={sess.moduleId} />


      {/* En sesiones gateadas los modales ni se montan (los botones que los
          abren tampoco se renderean — el transcript daría 403). */}
      {!gated ? (
        <>
          <SessionReplayModal
            open={replayOpen}
            sessionId={id}
            onClose={() => setReplayOpen(false)}
          />

          <SessionChatModal
            open={chatOpen}
            sessionId={id}
            onClose={() => setChatOpen(false)}
          />
        </>
      ) : null}
    </>
  );
}

function toggleSetMember(
  setter: React.Dispatch<React.SetStateAction<Set<string>>>,
  member: string,
): void {
  setter((prev) => {
    const next = new Set(prev);
    if (next.has(member)) next.delete(member);
    else next.add(member);
    return next;
  });
}

/**
 * Convierte un `span.close` del bus SSE al shape `TimelineSpan` que los
 * componentes ya saben renderear. Los SSE no traen `markupBps` ni
 * `sampleReason` (son datos del DB sink), los seteamos como null.
 */
function liveEventToTimelineSpan(ev: LiveSpanCloseEvent): TimelineSpan {
  return {
    id: ev.spanId,
    parentId: ev.parentSpanId,
    kind: ev.kind,
    name: ev.name,
    startedAt: ev.startedAt,
    endedAt: ev.endedAt,
    durationMs: ev.durationMs,
    costNano: ev.costNano,
    providerCostNano: ev.providerCostNano,
    markupBps: null,
    isError: ev.isError,
    errorKind: ev.errorKind,
    attributes: ev.attributes,
    sampleReason: null,
    callKind: ev.callKind,
    callRefId: ev.callRefId,
    // El bus no expone input/output como flag — si vinieran inline
    // (F5.1) los mostramos directo en el drawer. Por ahora hasIo=false
    // para no mostrar el botón "reveal" en spans que vinieron sólo por
    // bus (sin persistencia DB).
    hasIo: ev.input !== undefined || ev.output !== undefined,
  };
}
